import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Home() {
  const { t, i18n } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur z-40">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-xs font-bold">NH</span>
            </div>
            <span className="font-bold text-gray-900">{t('app_name')}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => i18n.changeLanguage(i18n.language === 'hi' ? 'en' : 'hi')}
              className="text-sm text-gray-600 hover:text-gray-900 border border-gray-200 px-3 py-1.5 rounded-lg"
            >
              {i18n.language === 'hi' ? 'English' : 'हिंदी'}
            </button>
            <Link to="/login" className="text-sm font-medium text-brand-600 hover:text-brand-700">
              {t('nav.login')}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-brand-900 via-brand-700 to-brand-500 text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl sm:text-5xl font-bold leading-tight">
            {i18n.language === 'hi' ? 'मजदूर से मालिक तक — सीधा सेतु' : 'Connect Workers to Employers — Directly'}
          </h1>
          <p className="mt-4 text-lg text-brand-100 max-w-xl mx-auto">
            {i18n.language === 'hi'
              ? 'मात्र ₹10 में पंजीकरण करें। अपनी पहचान बनाएं। बेहतर काम पाएं।'
              : 'Register for just ₹10. Build your verified identity. Get better work.'}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login"
              className="bg-white text-brand-700 font-semibold px-8 py-3.5 rounded-xl hover:bg-brand-50 transition-colors text-center">
              👷 {i18n.language === 'hi' ? 'मजदूर के रूप में पंजीकरण' : 'Register as Worker'}
            </Link>
            <Link to="/login"
              className="border-2 border-white text-white font-semibold px-8 py-3.5 rounded-xl hover:bg-white/10 transition-colors text-center">
              🏢 {i18n.language === 'hi' ? 'नियोक्ता के रूप में' : 'Register as Employer'}
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 max-w-5xl mx-auto">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-12">
          {i18n.language === 'hi' ? 'यह कैसे काम करता है' : 'How It Works'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {[
            { icon: '📝', step: '1', title: i18n.language === 'hi' ? 'पंजीकरण करें' : 'Register', desc: i18n.language === 'hi' ? 'मोबाइल से OTP से मात्र ₹10 में पंजीकरण' : 'Register with mobile OTP for just ₹10' },
            { icon: '✅', step: '2', title: i18n.language === 'hi' ? 'प्रोफाइल बनाएं' : 'Build Profile', desc: i18n.language === 'hi' ? 'कौशल, अनुभव और प्राथमिकताएं भरें' : 'Add skills, experience and preferences' },
            { icon: '💼', step: '3', title: i18n.language === 'hi' ? 'काम पाएं' : 'Get Work', desc: i18n.language === 'hi' ? 'मेल खाने वाली नौकरियों की सूचना पाएं' : 'Receive matched job notifications instantly' },
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">{item.icon}</span>
              </div>
              <div className="w-6 h-6 bg-brand-600 text-white text-xs font-bold rounded-full flex items-center justify-center mx-auto mb-3">
                {item.step}
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-500 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { value: '₹10', label: 'Registration Fee' },
            { value: '1 Year', label: 'Registration Valid' },
            { value: 'Instant', label: 'Job Matches' },
            { value: '100%', label: 'Verified Profiles' },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-2xl font-bold text-brand-400">{s.value}</p>
              <p className="text-sm text-gray-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-4 text-center text-sm text-gray-500">
        <p>© 2025 naukarihaat.com | नौकरी हाट | Made with ❤️ for India's workers</p>
      </footer>
    </div>
  );
}
